/**
 * Popsigns Spotlight uploader — Church + SBO unified intake.
 *
 * Wraps the `.sp-form` rendered by sections/spotlight-landing.liquid. Drives:
 *   - audience handling: reads ?industry=, drives [Church]/[Small Business] pills,
 *     keeps .sp-page[data-audience] in sync, swaps labels + placeholders,
 *     mirrors URL via history.replaceState, handles popstate
 *   - file picker / drag-drop with .sp-drop + .sp-file cards (progress, cancel,
 *     retry, remove)
 *   - S3 multipart upload via Lambda-minted presigned URLs (10MB parts, 4-way
 *     parallel, 2× retry per part)
 *   - submit gating: audience valid + fields valid + ≥1 done file + permission
 *   - confirmation reveal + scroll-into-view
 *   - sticky bottom CTA show/hide based on hero/upload viewport position
 *
 * Backend (relative to .sp-page[data-api-base]):
 *   POST /api/spotlight/init           — open multipart uploads
 *   POST /api/spotlight/presign-part   — mint URL for one part
 *   POST /api/spotlight/complete-upload— finalize one file
 *   POST /api/spotlight/finalize       — write metadata.json + send emails
 */
(function () {
  'use strict';

  if (window.customElements && customElements.get('spotlight-uploader')) return;

  // ---- upload constraints ----
  const MAX_FILES = 5;
  const MAX_FILE_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB
  const PART_SIZE = 10 * 1024 * 1024;            // 10 MB
  const PART_PARALLELISM = 4;
  const PART_RETRIES = 2;

  const ACCEPT_EXT = ['mp4', 'mov', 'hevc', 'heic', 'jpg', 'jpeg', 'png'];
  const EXT_TO_MIME = {
    mp4:  'video/mp4',
    mov:  'video/quicktime',
    hevc: 'video/hevc',
    heic: 'image/heic',
    heif: 'image/heif',
    jpg:  'image/jpeg',
    jpeg: 'image/jpeg',
    png:  'image/png',
  };

  const VALID_AUDIENCES = ['church', 'sbo'];

  // ---- helpers ----
  function getExt(name) {
    const m = String(name || '').match(/\.([a-z0-9]+)$/i);
    return m ? m[1].toLowerCase() : '';
  }
  function inferContentType(file) {
    if (file.type) return file.type;
    return EXT_TO_MIME[getExt(file.name)] || 'application/octet-stream';
  }
  function formatBytes(n) {
    if (n == null) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
    return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
  }
  function readQueryParams() {
    return new URLSearchParams(window.location.search);
  }
  function getSourceFromUrl(qp) {
    const p = qp.get('src');
    if (p === 'email') return 'outreach-email';
    if (p === 'ig') return 'ig-dm';
    return 'direct';
  }
  function getRootPage(el) { return el.closest('.sp-page'); }

  let _uid = 0;

  // -----------------------------------------------------------------------
  // Sticky CTA (page-level — initialized once at DOMContentLoaded)
  // -----------------------------------------------------------------------
  function initStickyCTA(root) {
    const sticky = root.querySelector('[data-sticky-cta]');
    const hero = root.querySelector('#top');
    const upload = root.querySelector('#upload');
    if (!sticky || !hero || !upload) return;
    const onScroll = () => {
      const heroBottom = hero.getBoundingClientRect().bottom;
      const uploadTop = upload.getBoundingClientRect().top;
      const show = heroBottom < 40 && uploadTop > window.innerHeight * 0.6;
      sticky.classList.toggle('is-show', show);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  // -----------------------------------------------------------------------
  // Page-level audience handler (drives data-audience on .sp-page,
  // updates label/placeholder swaps, mirrors URL, etc.)
  // -----------------------------------------------------------------------
  class SpotlightPage {
    constructor(root) {
      this.root = root;
      this.audience = '';
      this.audienceMethod = '';

      this.pills = Array.from(root.querySelectorAll('[data-set-audience]'));
      this.placeholderInputs = Array.from(root.querySelectorAll('[data-placeholder-church], [data-placeholder-sbo], [data-placeholder-neutral]'));

      this.uploader = root.querySelector('spotlight-uploader');

      this._bindPills();
      this._bindPopState();
      this._initFromUrl();
      initStickyCTA(root);
    }

    _bindPills() {
      this.pills.forEach((btn) => {
        btn.addEventListener('click', () => {
          const next = btn.dataset.setAudience;
          if (!VALID_AUDIENCES.includes(next)) return;
          const method = this.audience === next ? this.audienceMethod : 'manual-toggle';
          this.set(next, method || 'manual-toggle', /*updateUrl*/ true);
        });
      });
    }

    _bindPopState() {
      window.addEventListener('popstate', () => this._initFromUrl());
    }

    _initFromUrl() {
      const qp = readQueryParams();
      const raw = (qp.get('industry') || '').toLowerCase();
      if (VALID_AUDIENCES.includes(raw)) {
        this.set(raw, 'url-param', false);
      } else {
        this.set('', '', false);
      }
      // expose UTMs + outreach source for the uploader to pick up
      this.utm = {
        source: qp.get('utm_source') || null,
        campaign: qp.get('utm_campaign') || null,
        medium: qp.get('utm_medium') || null,
        content: qp.get('utm_content') || null,
        term: qp.get('utm_term') || null,
      };
      this.outreachSource = getSourceFromUrl(qp);
      if (this.uploader && this.uploader._setContext) {
        this.uploader._setContext({
          audience: this.audience,
          audienceMethod: this.audienceMethod,
          utm: this.utm,
          outreachSource: this.outreachSource,
        });
      }
    }

    set(value, method, updateUrl) {
      const audienceChanged = value !== this.audience;
      this.audience = value;
      this.audienceMethod = method;
      this.root.setAttribute('data-audience', value);
      this.root.setAttribute('data-audience-method', method);

      // Switching to a different audience invalidates the in-flight submission
      // (uploads under church/ can't be merged with sbo/). Reset and let files
      // re-upload under a fresh submission. Any in-flight uploads will fail
      // their next /init and surface an error to the user.
      if (audienceChanged && this.uploader && this.uploader._resetSubmission) {
        this.uploader._resetSubmission();
      }

      // pill aria-selected + is-on
      this.pills.forEach((btn) => {
        const on = btn.dataset.setAudience === value;
        btn.classList.toggle('is-on', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      // swap placeholders on inputs that have data-placeholder-*
      const key = value === 'church' ? 'placeholderChurch'
                 : value === 'sbo'    ? 'placeholderSbo'
                 : 'placeholderNeutral';
      this.placeholderInputs.forEach((input) => {
        const ph = input.dataset[key];
        if (ph) input.placeholder = ph;
      });

      // clear business_type value when leaving SBO so a stale value doesn't
      // get submitted with church/neutral
      if (value !== 'sbo' && this.uploader) {
        const bt = this.uploader.querySelector('input[name="business_type"]');
        if (bt) bt.value = '';
      }

      // mirror to URL
      if (updateUrl) {
        try {
          const url = new URL(window.location.href);
          if (value) url.searchParams.set('industry', value);
          else url.searchParams.delete('industry');
          window.history.replaceState(null, '', url);
        } catch (_) { /* ignore */ }
      }

      // notify the uploader so it can refresh its submit state
      if (this.uploader && this.uploader._setContext) {
        this.uploader._setContext({
          audience: this.audience,
          audienceMethod: this.audienceMethod,
          utm: this.utm,
          outreachSource: this.outreachSource,
        });
      }
    }
  }

  // -----------------------------------------------------------------------
  // The form / uploader custom element
  // -----------------------------------------------------------------------
  class SpotlightUploader extends HTMLElement {
    constructor() {
      super();
      this.files = [];
      this.submitting = false;
      this.submitted = false;
      this.audience = '';
      this.audienceMethod = '';
      this.utm = {};
      this.outreachSource = 'direct';
      // Single submission shared across all files. First file's /init creates
      // it; subsequent files append using submission_uuid + prefix.
      this.submissionUuid = null;
      this.submissionPrefix = null;
      this.nextFileIndex = 1;
      this._initChain = Promise.resolve();
    }

    connectedCallback() {
      this.root = getRootPage(this);
      this.apiBase = ((this.root && this.root.dataset.apiBase) || '').replace(/\/$/, '');

      this.form        = this.querySelector('.sp-form');
      this.dropzone    = this.querySelector('[data-dropzone]');
      this.fileInput   = this.querySelector('[data-file-input]');
      this.fileList    = this.querySelector('[data-file-list]');
      this.permission  = this.querySelector('[data-permission]');
      this.permRow     = this.querySelector('[data-permission-row]');
      this.submitBtn   = this.querySelector('[data-submit]');
      this.submitLabel = this.querySelector('[data-submit-label]');
      this.statusEl    = this.querySelector('[data-status]');
      this.statusMsg   = this.querySelector('[data-status-msg]');
      this.confirmEl   = this.parentElement.querySelector('[data-spotlight-confirm]');

      this._bindEvents();
      this._updateSubmitState();
    }

    _setContext(ctx) {
      if (ctx.audience !== undefined) this.audience = ctx.audience;
      if (ctx.audienceMethod !== undefined) this.audienceMethod = ctx.audienceMethod;
      if (ctx.utm) this.utm = ctx.utm;
      if (ctx.outreachSource) this.outreachSource = ctx.outreachSource;
      this._updateSubmitState();
    }

    // ---- events ----

    _bindEvents() {
      this.dropzone.addEventListener('click', (e) => {
        // only trigger file picker when clicking the dropzone itself, not children
        if (e.target.closest('.sp-file__act')) return;
        this.fileInput.click();
      });
      this.dropzone.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.fileInput.click();
        }
      });

      ['dragenter', 'dragover'].forEach((ev) => {
        this.dropzone.addEventListener(ev, (e) => {
          e.preventDefault();
          this.dropzone.classList.add('is-over');
        });
      });
      ['dragleave', 'drop'].forEach((ev) => {
        this.dropzone.addEventListener(ev, (e) => {
          e.preventDefault();
          this.dropzone.classList.remove('is-over');
        });
      });
      this.dropzone.addEventListener('drop', (e) => {
        if (e.dataTransfer && e.dataTransfer.files) {
          this._addFiles(e.dataTransfer.files);
        }
      });

      this.fileInput.addEventListener('change', (e) => {
        this._addFiles(e.target.files);
        this.fileInput.value = '';
      });

      this.permission.addEventListener('change', () => {
        if (this.permRow) {
          this.permRow.classList.toggle('is-checked', this.permission.checked);
          this.permRow.classList.remove('is-error');
        }
        this._updateSubmitState();
      });

      ['organization_name', 'contact_name', 'contact_email', 'city_state'].forEach((name) => {
        const el = this.form.elements[name];
        if (el) {
          el.addEventListener('input', () => {
            el.classList.remove('is-error');
            this._clearErr(name);
            this._updateSubmitState();
          });
        }
      });
      const bt = this.form.elements.business_type;
      if (bt) {
        bt.addEventListener('input', () => {
          bt.classList.remove('is-error');
          this._clearErr('business_type');
          this._updateSubmitState();
        });
      }

      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this._submit();
      });
    }

    // ---- file staging ----

    _addFiles(fileList) {
      if (this.submitting || this.submitted) return;
      const remaining = MAX_FILES - this.files.length;
      const candidates = Array.from(fileList).slice(0, remaining);

      for (const f of candidates) {
        const ext = getExt(f.name);
        const isVideo = (f.type && f.type.startsWith('video/')) || ACCEPT_EXT.includes(ext);
        const id = ++_uid;

        if (!ACCEPT_EXT.includes(ext)) {
          this.files.push({
            id, file: f, name: f.name, size: f.size || 0,
            contentType: 'application/octet-stream', bytesUploaded: 0,
            status: 'error', errorMsg: "Not a supported file type — vertical .mov, .mp4, .heic, .jpg, or .png.",
            xhrs: [],
          });
          continue;
        }
        if (f.size > MAX_FILE_BYTES) {
          this.files.push({
            id, file: f, name: f.name, size: f.size,
            contentType: inferContentType(f), bytesUploaded: 0,
            status: 'error', errorMsg: 'File is over 2 GB — try a shorter clip.',
            xhrs: [],
          });
          continue;
        }
        if (f.size === 0) {
          this.files.push({
            id, file: f, name: f.name, size: 0,
            contentType: inferContentType(f), bytesUploaded: 0,
            status: 'error', errorMsg: 'File is empty.',
            xhrs: [],
          });
          continue;
        }

        const fwrap = {
          id, file: f, name: f.name, size: f.size,
          contentType: inferContentType(f),
          bytesUploaded: 0,
          status: 'queued',
          errorMsg: null,
          fileId: null, s3Key: null, uploadId: null,
          parts: [], xhrs: [],
        };
        this.files.push(fwrap);
        // kick off upload right away (so progress bar advances + button enables when done)
        setTimeout(() => this._uploadFile(fwrap), 200);
      }

      if (fileList.length > remaining) {
        this._setStatus(`You can add up to ${MAX_FILES} clips. Extras weren’t added.`, 'error');
      }
      this._renderFiles();
      this._updateSubmitState();
    }

    _removeFile(id) {
      const f = this.files.find((x) => x.id === id);
      if (!f) return;
      f.xhrs.forEach((xhr) => { try { xhr.abort(); } catch (_) {} });
      this.files = this.files.filter((x) => x.id !== id);
      this._renderFiles();
      this._updateSubmitState();
    }

    _retryFile(id) {
      const f = this.files.find((x) => x.id === id);
      if (!f) return;
      if (f.errorMsg && /not a supported|not a video|over 2 gb|empty/i.test(f.errorMsg)) {
        this._setStatus("That file can’t be retried — remove it and pick a vertical .mov or .mp4.", 'error');
        return;
      }
      f.status = 'queued';
      f.errorMsg = null;
      f.bytesUploaded = 0;
      this._renderFiles();
      setTimeout(() => this._uploadFile(f), 100);
    }

    _renderFiles() {
      this.fileList.hidden = this.files.length === 0;
      this.fileList.innerHTML = '';
      // Drives the .sp-nudge "don't forget the other clip" visibility — CSS
      // shows it only when exactly 1 file is present.
      this.setAttribute('data-file-count', String(this.files.length));

      this.files.forEach((f) => {
        const li = document.createElement('div');
        li.className = 'sp-file';
        if (f.status === 'done') li.classList.add('is-done');
        if (f.status === 'error') li.classList.add('is-error');

        const pct = f.size > 0 ? Math.min(100, Math.round((f.bytesUploaded / f.size) * 100)) : 0;

        const thumbIcon =
          f.status === 'done'  ? 'ph-check-circle'
          : f.status === 'error' ? 'ph-warning'
          : 'ph-film-strip';

        let metaHtml = '';
        if (f.status === 'queued') {
          metaHtml = `<span class="stat">Queued&hellip;</span>`;
        } else if (f.status === 'uploading') {
          metaHtml = `<span class="stat">${formatBytes(f.bytesUploaded)}</span> / ${formatBytes(f.size)} &middot; ${pct}%`;
        } else if (f.status === 'done') {
          metaHtml = `<span class="stat ok">Uploaded</span> &middot; ${formatBytes(f.size)}`;
        } else if (f.status === 'error') {
          metaHtml = `<span class="stat err">${escapeHtml(f.errorMsg || 'Upload failed')}</span>`;
        }

        const showBar = f.status !== 'error';
        const isUp = f.status === 'uploading' || f.status === 'queued';

        let actionsHtml = '';
        if (isUp) {
          actionsHtml = `<button type="button" class="sp-file__act" data-act="cancel" aria-label="Cancel upload" title="Cancel"><i class="ph-bold ph-x"></i></button>`;
        } else if (f.status === 'error') {
          actionsHtml = `<button type="button" class="sp-file__act sp-file__retry" data-act="retry" aria-label="Retry" title="Retry"><i class="ph-bold ph-arrow-clockwise"></i></button>`;
        } else if (f.status === 'done') {
          actionsHtml = `<button type="button" class="sp-file__act" data-act="remove" aria-label="Remove" title="Remove"><i class="ph-bold ph-trash"></i></button>`;
        }

        li.innerHTML = `
          <span class="sp-file__thumb"><i class="ph-fill ${thumbIcon}"></i></span>
          <div class="sp-file__main">
            <div class="sp-file__name"></div>
            <div class="sp-file__meta">${metaHtml}</div>
            ${showBar ? `<div class="sp-bar"><div class="sp-bar__fill" style="width:${f.status === 'done' ? 100 : pct}%"></div></div>` : ''}
          </div>
          ${actionsHtml}
        `;
        li.querySelector('.sp-file__name').textContent = f.name;

        const actBtn = li.querySelector('[data-act]');
        if (actBtn) {
          actBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            const a = actBtn.dataset.act;
            if (a === 'cancel' || a === 'remove') this._removeFile(f.id);
            else if (a === 'retry') this._retryFile(f.id);
          });
        }
        this.fileList.appendChild(li);
      });

      // update aggregate status
      const uploading = this.files.filter((f) => f.status === 'uploading' || f.status === 'queued').length;
      const done = this.files.filter((f) => f.status === 'done').length;
      if (this.submitting) return;
      if (uploading > 0) {
        this._setStatus(`Uploading ${uploading} clip${uploading > 1 ? 's' : ''}… ${done > 0 ? done + ' ready' : ''}`, 'info', 'ph-spinner-gap');
      } else if (done > 0) {
        this._setStatus(`${done} clip${done > 1 ? 's' : ''} ready to submit.`, 'info', 'ph-check-circle');
      } else if (this.files.length === 0) {
        this._clearStatus();
      } else {
        this._clearStatus();
      }
    }

    // ---- form state ----

    _updateSubmitState() {
      // Audience gating is no longer a submit blocker — if the user never
      // picks church or sbo, we silently default to 'church' at upload + submit
      // time and tag audience_selection_method='default-fallback' so admin can
      // reclassify if needed. Keeps cold submissions flowing instead of dead-ending.
      const doneCount = this.files.filter((f) => f.status === 'done').length;
      const ok =
        !this.submitting &&
        !this.submitted &&
        doneCount >= 1 &&
        this.permission.checked &&
        this._allRequiredFilled();
      this.submitBtn.disabled = !ok;
    }

    // Silently set audience to 'church' (historical majority) when unset, so
    // cold-lander submissions don't dead-end. We do NOT update page-level
    // data-audience or toggle pills — the page UI stays in its neutral state,
    // only the request payload gets the default. Admin sees this via
    // audience_selection_method='default-fallback' in the metadata + email.
    _applyDefaultAudienceIfUnset() {
      if (VALID_AUDIENCES.includes(this.audience)) return false;
      this.audience = 'church';
      this.audienceMethod = 'default-fallback';
      return true;
    }

    _allRequiredFilled() {
      const req = ['organization_name', 'contact_name', 'contact_email', 'city_state'];
      for (const name of req) {
        const v = (this.form.elements[name].value || '').trim();
        if (!v) return false;
      }
      if (this.audience === 'sbo') {
        const bt = this.form.elements.business_type;
        if (!bt || !(bt.value || '').trim()) return false;
      }
      const email = (this.form.elements.contact_email.value || '').trim();
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    _setStatus(msg, kind, iconName) {
      if (!this.statusEl) return;
      this.statusEl.hidden = false;
      this.statusEl.classList.toggle('sp-status--error', kind === 'error');
      this.statusMsg.textContent = msg || '';
      const icon = this.statusEl.querySelector('i');
      if (icon) {
        icon.className = 'ph-fill ' + (iconName || (kind === 'error' ? 'ph-warning-circle' : 'ph-info'));
      }
    }
    _clearStatus() {
      if (!this.statusEl) return;
      this.statusEl.hidden = true;
      this.statusEl.classList.remove('sp-status--error');
      this.statusMsg.textContent = '';
    }

    _markErr(name, msg) {
      const input = this.form.elements[name];
      if (input) input.classList.add('is-error');
      const line = this.querySelector(`[data-error-for="${name}"]`);
      if (line) {
        line.hidden = false;
        const span = line.querySelector('span');
        if (span) span.textContent = msg;
      }
    }
    _clearErr(name) {
      const line = this.querySelector(`[data-error-for="${name}"]`);
      if (line) {
        line.hidden = true;
        const span = line.querySelector('span');
        if (span) span.textContent = '';
      }
    }

    // ---- submit ----

    async _submit() {
      if (this.submitting || this.submitted) return;
      this._applyDefaultAudienceIfUnset();

      // client-side field validation
      let valid = true;
      const reqFields = {
        organization_name: 'This field is required.',
        contact_name: 'This field is required.',
        contact_email: 'This field is required.',
        city_state: 'This field is required.',
      };
      if (this.audience === 'sbo') reqFields.business_type = 'This field is required.';

      Object.keys(reqFields).forEach((name) => {
        const v = (this.form.elements[name].value || '').trim();
        if (!v) { this._markErr(name, reqFields[name]); valid = false; }
        else this._clearErr(name);
      });
      const email = (this.form.elements.contact_email.value || '').trim();
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this._markErr('contact_email', 'Enter a complete email address.');
        valid = false;
      }

      const doneCount = this.files.filter((f) => f.status === 'done').length;
      if (doneCount < 1) {
        this._setStatus('Add at least one clip before submitting.', 'error');
        return;
      }
      if (!this.permission.checked) {
        if (this.permRow) this.permRow.classList.add('is-error');
        valid = false;
      }
      if (!valid) {
        this._setStatus('Please fix the highlighted fields and try again.', 'error');
        return;
      }

      this.submitting = true;
      this.submitBtn.disabled = true;
      this.submitLabel.innerHTML = '<span class="sp-btn__spin"></span> Sending…';
      this._setStatus('Submitting…', 'info', 'ph-spinner-gap');

      try {
        const succeededFiles = this.files.filter((f) => f.status === 'done');
        if (!this.submissionUuid || !this.submissionPrefix) {
          throw new Error('no submission active — re-upload your files');
        }

        const finalResp = await this._postJson('/api/spotlight/finalize', {
          submission_uuid: this.submissionUuid,
          prefix: this.submissionPrefix,
          audience: this.audience,
          audience_selection_method: this.audienceMethod || 'manual-toggle',
          organization_name: this.form.elements.organization_name.value.trim(),
          business_type: this.audience === 'sbo'
            ? (this.form.elements.business_type.value.trim() || null)
            : null,
          contact_name: this.form.elements.contact_name.value.trim(),
          contact_email: this.form.elements.contact_email.value.trim(),
          instagram_handle: this.form.elements.instagram_handle.value.trim() || null,
          city_state: this.form.elements.city_state.value.trim(),
          moment_note: this.form.elements.moment_note.value.trim() || null,
          permission_granted: this.permission.checked === true,
          source: this.outreachSource,
          utm_source: this.utm.source,
          utm_campaign: this.utm.campaign,
          utm_medium: this.utm.medium,
          utm_content: this.utm.content,
          utm_term: this.utm.term,
          files: succeededFiles.map((f) => ({
            s3_key: f.s3Key,
            original_name: f.name,
            content_type: f.contentType,
          })),
        });
        if (!finalResp.ok) throw new Error(finalResp.error || 'Finalize failed');

        this._showConfirmation();
      } catch (err) {
        console.error('[Spotlight] submit failed', err);
        this._setStatus(
          err && err.message
            ? `Couldn’t submit: ${err.message}. Please try again or email sam@popsigns.co.`
            : 'Couldn’t submit. Please try again.',
          'error'
        );
        this.submitting = false;
        this.submitLabel.textContent = 'Submit my clip';
        this._updateSubmitState();
      }
    }

    // ---- multipart upload primitives ----

    // Serialize init calls so exactly ONE creates the submission and subsequent
    // files append. Each file's init waits for the previous file's init to
    // settle before issuing its own.
    _initForFile(fwrap) {
      const myInit = this._initChain.then(async () => {
        const isFirst = !this.submissionUuid;
        const body = {
          audience: this.audience,
          files: [{
            name: fwrap.name,
            content_type: fwrap.contentType,
            size_bytes: fwrap.size,
          }],
        };
        if (isFirst) {
          body.organization_name = (this.form.elements.organization_name.value || '').trim() || 'submission';
        } else {
          body.submission_uuid = this.submissionUuid;
          body.prefix = this.submissionPrefix;
          body.file_index_start = this.nextFileIndex;
        }
        const resp = await this._postJson('/api/spotlight/init', body);
        if (!resp.ok) throw new Error(resp.error || 'init failed');
        if (isFirst) {
          this.submissionUuid = resp.submission_uuid;
          this.submissionPrefix = resp.prefix;
          this.nextFileIndex = 2;
        } else {
          this.nextFileIndex += 1;
        }
        return resp;
      });
      // Don't let one file's init error block subsequent files from
      // starting; surface the error to the caller via myInit but keep the
      // chain alive for the next file.
      this._initChain = myInit.catch(() => {});
      return myInit;
    }

    // Reset submission state — called when audience changes so a new
    // submission gets created on the next file upload.
    _resetSubmission() {
      this.submissionUuid = null;
      this.submissionPrefix = null;
      this.nextFileIndex = 1;
      this._initChain = Promise.resolve();
    }

    async _uploadFile(fwrap) {
      // Apply default audience if unset — keeps cold uploads flowing instead
      // of polling for a manual pick that may never come.
      this._applyDefaultAudienceIfUnset();

      try {
        const initResp = await this._initForFile(fwrap);

        fwrap.submissionUuid = initResp.submission_uuid;
        fwrap.prefix = initResp.prefix;
        fwrap.fileId = initResp.files[0].file_id;
        fwrap.s3Key  = initResp.files[0].s3_key;
        fwrap.uploadId = initResp.files[0].upload_id;

        fwrap.status = 'uploading';
        fwrap.bytesUploaded = 0;
        this._renderFiles();

        const totalParts = Math.max(1, Math.ceil(fwrap.size / PART_SIZE));
        const partsCompleted = new Array(totalParts).fill(null);
        const partBytes = new Array(totalParts).fill(0);

        let nextPart = 1;
        const workers = [];
        let failure = null;

        const updateProgress = () => {
          fwrap.bytesUploaded = partBytes.reduce((a, b) => a + b, 0);
          this._renderFiles();
        };

        for (let w = 0; w < Math.min(PART_PARALLELISM, totalParts); w++) {
          workers.push((async () => {
            while (!failure) {
              const partNumber = nextPart++;
              if (partNumber > totalParts) return;
              const start = (partNumber - 1) * PART_SIZE;
              const end = Math.min(start + PART_SIZE, fwrap.size);
              const blob = fwrap.file.slice(start, end);

              let attempt = 0;
              while (attempt <= PART_RETRIES) {
                try {
                  const presign = await this._postJson('/api/spotlight/presign-part', {
                    submission_uuid: fwrap.submissionUuid,
                    s3_key: fwrap.s3Key,
                    upload_id: fwrap.uploadId,
                    part_number: partNumber,
                  });
                  if (!presign.ok) throw new Error(presign.error || 'presign failed');

                  const etag = await this._putPart(presign.url, blob, (uploaded) => {
                    partBytes[partNumber - 1] = uploaded;
                    updateProgress();
                  }, fwrap);

                  partsCompleted[partNumber - 1] = { PartNumber: partNumber, ETag: etag };
                  partBytes[partNumber - 1] = blob.size;
                  updateProgress();
                  break;
                } catch (e) {
                  attempt += 1;
                  if (attempt > PART_RETRIES) { failure = e; return; }
                  await new Promise((r) => setTimeout(r, 500 * attempt));
                }
              }
            }
          })());
        }

        await Promise.all(workers);
        if (failure) throw failure;

        const completeResp = await this._postJson('/api/spotlight/complete-upload', {
          submission_uuid: fwrap.submissionUuid,
          s3_key: fwrap.s3Key,
          upload_id: fwrap.uploadId,
          parts: partsCompleted,
        });
        if (!completeResp.ok) throw new Error(completeResp.error || 'complete failed');

        fwrap.status = 'done';
        fwrap.bytesUploaded = fwrap.size;
        this._renderFiles();
        this._updateSubmitState();
      } catch (err) {
        console.error('[Spotlight] upload failed', err);
        fwrap.status = 'error';
        fwrap.errorMsg = (err && err.message) || 'Upload failed';
        this._renderFiles();
        this._updateSubmitState();
      }
    }

    _putPart(url, blob, onProgress, fwrap) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        fwrap.xhrs.push(xhr);
        xhr.open('PUT', url, true);
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) onProgress(e.loaded);
        });
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const etag = xhr.getResponseHeader('ETag') || xhr.getResponseHeader('etag');
            if (!etag) {
              reject(new Error('S3 did not return an ETag (check bucket CORS ExposeHeaders)'));
              return;
            }
            resolve(etag.replace(/^"|"$/g, ''));
          } else {
            reject(new Error(`S3 PUT returned ${xhr.status}`));
          }
        });
        xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
        xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));
        xhr.send(blob);
      });
    }

    async _postJson(path, body) {
      const url = this.apiBase + path;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      let data = null;
      try { data = await res.json(); } catch (_) { /* non-JSON */ }
      if (!res.ok) {
        const msg = (data && (data.error || data.message)) || `Request failed (${res.status})`;
        throw new Error(msg);
      }
      return data;
    }

    _showConfirmation() {
      this.submitted = true;
      this.submitting = false;
      this.form.hidden = true;
      if (this.confirmEl) {
        this.confirmEl.hidden = false;
        if (this.confirmEl.scrollIntoView) {
          this.confirmEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
      this._clearStatus();
    }
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  customElements.define('spotlight-uploader', SpotlightUploader);

  // Initialise the audience handler for any .sp-page on the document.
  function initPages() {
    document.querySelectorAll('.sp-page').forEach((root) => {
      if (root.__spotlightInitialized) return;
      root.__spotlightInitialized = true;
      // Defer one tick so custom elements finish upgrading
      setTimeout(() => new SpotlightPage(root), 0);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPages, { once: true });
  } else {
    initPages();
  }
})();
